import Graphic from '@arcgis/core/Graphic.js';
import type GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import {
  bringOverlayLayersToTop,
  getBufferLayer,
  getHighlightLayer,
  getMarkerLayer,
} from '@/map/mapStore';
import { resolve, type Source, SourceSchema } from '@/agent/refs';
import {
  BUFFER_FILL,
  HIGHLIGHT_FILL,
  HIGHLIGHT_LINE,
  HIGHLIGHT_POINT,
  MARKER_POINT,
  symbolForGeometry,
} from '@/map/symbols';
import type { Tool } from './types';

type Style = 'highlight' | 'marker' | 'buffer' | 'outline';
export type Bucket = 'highlights' | 'markers' | 'buffers';

type Input = {
  source: Source;
  style: Style;
  bucket?: Bucket;
  clear_first?: boolean;
  // When the source resolves to more than RENDER_SOFT_CAP geometries the call
  // returns `requires_confirmation` instead of drawing. The agent re-issues
  // with `force: true` once it has the user's blessing.
  force?: boolean;
};

const BUCKET_DEFAULTS: Record<Style, Bucket> = {
  highlight: 'highlights',
  outline: 'highlights',
  marker: 'markers',
  buffer: 'buffers',
};

const RENDER_SOFT_CAP = 200;

// One stack of "what was drawn in the last render call" per bucket. Each
// entry is the exact list of Graphics added by that call so render_undo can
// remove them precisely without nuking older overlays.
const renderHistory: Record<Bucket, Graphic[][]> = {
  highlights: [],
  markers: [],
  buffers: [],
};

export function popLastRenderBatch(bucket: Bucket): Graphic[] | null {
  return renderHistory[bucket].pop() ?? null;
}

export function clearRenderHistory(bucket?: Bucket): void {
  if (!bucket) {
    renderHistory.highlights = [];
    renderHistory.markers = [];
    renderHistory.buffers = [];
    return;
  }
  renderHistory[bucket] = [];
}

export const render: Tool<Input> = {
  name: 'render',
  description:
    `Draw a Source on the map. style chooses the symbol family; bucket groups graphics so render_clear can wipe just one set. clear_first removes existing graphics in the same bucket before drawing. If the source resolves to more than ${RENDER_SOFT_CAP} geometries the call returns { requires_confirmation: true, would_draw: N } instead of drawing — ask the user to confirm or narrow the scope, then re-call with force: true. Pair with render_undo to remove the last batch you drew.`,
  input_schema: {
    type: 'object',
    properties: {
      source: SourceSchema,
      style: { enum: ['highlight', 'marker', 'buffer', 'outline'] },
      bucket: { enum: ['highlights', 'markers', 'buffers'] },
      clear_first: { type: 'boolean' },
      force: {
        type: 'boolean',
        description:
          'Bypass the soft cap. Use only after the user confirms a large render is intended.',
      },
    },
    required: ['source', 'style'],
    additionalProperties: false,
  },
  execute: async ({ source, style, bucket, clear_first, force }) => {
    const ref = await resolve(source);
    const target = bucket ?? BUCKET_DEFAULTS[style];
    const layer = bucketLayer(target);
    if (!layer) throw new Error(`render: graphics bucket "${target}" not initialised`);

    const geometries = collectGeometries(ref);
    if (geometries.length === 0) throw new Error('render: source produced no geometry');

    if (geometries.length > RENDER_SOFT_CAP && !force) {
      return {
        requires_confirmation: true,
        would_draw: geometries.length,
        bucket: target,
        soft_cap: RENDER_SOFT_CAP,
        hint:
          `That would add ${geometries.length} graphics to the "${target}" bucket — likely visually noisy. Confirm with the user, then re-call with force: true. Or narrow the source first.`,
      };
    }

    if (clear_first) {
      layer.removeAll();
      clearRenderHistory(target);
    }

    const drawnGraphics: Graphic[] = [];
    for (const geom of geometries) {
      const symbol = pickSymbol(style, geom.type);
      const g = new Graphic({ geometry: geom, symbol });
      layer.add(g);
      drawnGraphics.push(g);
    }
    renderHistory[target].push(drawnGraphics);

    bringOverlayLayersToTop();

    return { bucket: target, drawn: drawnGraphics.length };
  },
};

export function bucketLayer(bucket: Bucket): GraphicsLayer | null {
  switch (bucket) {
    case 'highlights':
      return getHighlightLayer();
    case 'markers':
      return getMarkerLayer();
    case 'buffers':
      return getBufferLayer();
  }
}

function collectGeometries(ref: { geometry?: __esri.Geometry; feature_set?: { geometries: __esri.Geometry[] } }): __esri.Geometry[] {
  if (ref.feature_set && ref.feature_set.geometries.length > 0) {
    return ref.feature_set.geometries;
  }
  if (ref.geometry) return [ref.geometry];
  return [];
}

function pickSymbol(style: Style, geometryType: __esri.Geometry['type']) {
  if (style === 'marker') return MARKER_POINT;
  if (style === 'buffer') return BUFFER_FILL;
  if (style === 'outline') return symbolForGeometry(geometryType, 'highlight');
  // highlight
  switch (geometryType) {
    case 'point':
    case 'multipoint':
      return HIGHLIGHT_POINT;
    case 'polyline':
      return HIGHLIGHT_LINE;
    default:
      return HIGHLIGHT_FILL;
  }
}
